import { useEffect, useRef, useState, useCallback } from "react";

const PREFIX = "td_draft:";
const VERSION = 1;
const DEBOUNCE_MS = 800;
const MIN_CHANGE_DELAY_MS = 300;

function readDraft(key) {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.v !== VERSION) return null;
    return parsed;
  } catch (err) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("[useLocalDraft] readDraft failed (corrupted JSON?):", err?.message);
    }
    return null;
  }
}

function writeDraft(key, data) {
  try {
    localStorage.setItem(PREFIX + key, JSON.stringify({
      v: VERSION,
      savedAt: Date.now(),
      data,
    }));
    return true;
  } catch (err) {
    // Quota exceeded is the typical case here (5-10 MB localStorage limit hit).
    if (process.env.NODE_ENV !== "production") {
      console.warn("[useLocalDraft] writeDraft failed (quota exceeded?):", err?.message);
    }
    return false;
  }
}

function clearDraft(key) {
  try {
    localStorage.removeItem(PREFIX + key);
  } catch (err) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("[useLocalDraft] clearDraft failed:", err?.message);
    }
  }
}

/**
 * Local draft hook (persistent across reloads / network drops).
 *
 * Usage:
 *   const draft = useLocalDraft({ key: `inventory:${id || 'new'}`, form, enabled: isEditable, isDirty });
 *   draft.savedAt           // ms timestamp of last successful local save (or null)
 *   draft.pendingRestore    // { savedAt, data } | null — present at mount if a stale draft exists
 *   draft.restore()         // returns the saved form data (and dismisses pendingRestore)
 *   draft.dismiss()         // discards pendingRestore (keeps the disk copy until next change)
 *   draft.clear()           // wipes the draft from disk (call after successful server save)
 */
export function useLocalDraft({ key, form, enabled = true, serverUpdatedAt = null }) {
  const [savedAt, setSavedAt] = useState(null);
  const [pendingRestore, setPendingRestore] = useState(null);
  const dismissedRef = useRef(false);
  const lastWriteRef = useRef(0);
  const baselineRef = useRef(null); // last form snapshot considered "in sync with server"

  // On mount or key change: load existing draft into pendingRestore if newer than server data
  useEffect(() => {
    if (!key) return;
    const existing = readDraft(key);
    if (!existing) {
      setPendingRestore(null);
      setSavedAt(null);
      return;
    }
    const serverTs = serverUpdatedAt ? new Date(serverUpdatedAt).getTime() : 0;
    if (serverTs && existing.savedAt < serverTs) {
      clearDraft(key);
      setPendingRestore(null);
      setSavedAt(null);
      return;
    }
    setPendingRestore(existing);
    setSavedAt(existing.savedAt);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Debounced auto-save on form change. Skip when form is identical to the latest baseline (in-sync).
  useEffect(() => {
    if (!enabled || !key || !form) return;
    const handle = setTimeout(() => {
      const now = Date.now();
      if (now - lastWriteRef.current < MIN_CHANGE_DELAY_MS) return;
      // Skip if form matches the in-sync baseline (avoids re-creating draft right after a server save)
      if (baselineRef.current && JSON.stringify(form) === baselineRef.current) return;
      const ok = writeDraft(key, form);
      if (ok) {
        lastWriteRef.current = now;
        setSavedAt(now);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [form, enabled, key]);

  const restore = useCallback(() => {
    if (!pendingRestore) return null;
    const data = pendingRestore.data;
    setPendingRestore(null);
    return data;
  }, [pendingRestore]);

  const dismiss = useCallback(() => {
    dismissedRef.current = true;
    setPendingRestore(null);
  }, []);

  const clear = useCallback((syncedForm) => {
    if (key) clearDraft(key);
    setSavedAt(null);
    setPendingRestore(null);
    // Mark the current form as the in-sync baseline → next auto-save will only fire on real changes
    if (syncedForm !== undefined) {
      baselineRef.current = JSON.stringify(syncedForm);
    }
  }, [key]);

  return { savedAt, pendingRestore, restore, dismiss, clear };
}

export function formatRelativeTime(ms) {
  if (!ms) return "";
  const diff = Math.max(0, Date.now() - ms) / 1000;
  if (diff < 5) return "ora";
  if (diff < 60) return `${Math.round(diff)} sec fa`;
  if (diff < 3600) return `${Math.round(diff / 60)} min fa`;
  if (diff < 86400) return `${Math.round(diff / 3600)} h fa`;
  return new Date(ms).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
}
